import { useQuery } from "@tanstack/react-query";

import { getJobDetailPublicApi, getJobListPublicApi } from "api/services/jobs";
import { performRequest } from "api/performRequest";

import { isClosed } from "./lib/dates";
import { DEMO_ENABLED, DEMO_JOB, isDemoSlug } from "./lib/demo";
import { sortQuestions } from "./lib/questions";

const FIVE_MINUTES = 5 * 60 * 1000;

export const careersKeys = {
  jobs: ["careers", "jobs"],
  job: (slug) => ["careers", "job", slug],
};

// Open roles, newest first. A role past its closing date is still "open" in
// the admin until someone closes it, so it is filtered out here.
export function useOpenJobs() {
  return useQuery({
    queryKey: careersKeys.jobs,
    queryFn: async () => {
      const { data } = await getJobListPublicApi({ limit: 200 });
      const open = (data?.results ?? data ?? [])
        .filter((job) => !isClosed(job.expiry_date))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return DEMO_ENABLED ? [...open, DEMO_JOB] : open;
    },
    staleTime: FIVE_MINUTES,
  });
}

// One role with its screening questions. A 404 means closed or never
// existed; the page shows that as "this role has closed", not an error.
export function useJob(slug) {
  return useQuery({
    queryKey: careersKeys.job(slug),
    queryFn: async () => {
      if (isDemoSlug(slug)) return DEMO_JOB;
      try {
        const { data } = await getJobDetailPublicApi(slug, {});
        return { ...data, questions: sortQuestions(data.questions) };
      } catch (error) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    },
    enabled: Boolean(slug),
    staleTime: FIVE_MINUTES,
    retry: (count, error) => error?.response?.status !== 404 && count < 1,
  });
}

// Has this email been verified within the server's one-hour window? Used to
// decide whether a restored draft still counts as verified.
export async function checkEmailVerified(email) {
  try {
    const { data } = await performRequest(
      "get",
      "/api/public/email-verification/status/",
      { email },
      false,
    );
    // create_success_response puts its fields at the top level.
    return Boolean(data?.is_verified);
  } catch {
    return false;
  }
}

export const apiError = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || fallback;
